import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { motion } from "framer-motion";
import { ArrowLeft, Check, Sparkles } from "lucide-react";
import templatesData from "../lib/templates.json";

const Templates = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState(templatesData);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Templates are now loaded statically
  }, []);

  const categories = ["All", ...new Set(templates.map((t) => t.category))];

  const filteredTemplates =
    selectedCategory === "All"
      ? templates
      : templates.filter((t) => t.category === selectedCategory);

  const handleContinue = () => {
    if (selectedTemplate) {
      navigate(`/editor?template=${selectedTemplate.id}`);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border/50">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/">
              <Button variant="ghost" size="icon" className="rounded-full">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            </Link>
            <span className="font-heading text-xl font-bold">Choose Template</span>
          </div>
          <Button
            className="rounded-full bg-primary text-primary-foreground"
            disabled={!selectedTemplate}
            onClick={handleContinue}
            data-testid="continue-to-editor-btn"
          >
            Continue to Editor
          </Button>
        </div>
      </header>

      {/* Category Filters */}
      <div className="border-b border-border">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? "default" : "outline"}
                className="rounded-full whitespace-nowrap"
                onClick={() => setSelectedCategory(category)}
                data-testid={`category-${category.toLowerCase()}`}
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </div>

      {/* Templates Grid */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {isLoading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="bg-secondary animate-pulse rounded-2xl h-48" />
            ))}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredTemplates.map((template, index) => (
              <motion.div
                key={template.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
              >
                <button
                  className={`w-full text-left rounded-2xl overflow-hidden transition-all duration-300 ${selectedTemplate?.id === template.id
                      ? "ring-2 ring-primary ring-offset-2 shadow-xl scale-[1.02]"
                      : "modern-card hover:scale-[1.01]"
                    }`}
                  onClick={() => setSelectedTemplate(template)}
                  data-testid={`template-${template.id}`}
                >
                  {/* Card Preview */}
                  <div
                    className="aspect-[3.5/2] p-5 relative"
                    style={{ backgroundColor: template.secondary_color }}
                  >
                    {/* Selection indicator */}
                    {selectedTemplate?.id === template.id && (
                      <div className="absolute top-3 right-3 w-6 h-6 bg-primary text-primary-foreground rounded-full flex items-center justify-center">
                        <Check className="w-4 h-4" />
                      </div>
                    )}

                    {/* Template preview content */}
                    <div
                      className="w-6 h-6 rounded-md mb-3"
                      style={{ backgroundColor: template.primary_color }}
                    />
                    <div style={{ color: template.primary_color }}>
                      <p
                        className="text-sm font-bold truncate mb-1"
                        style={{ fontFamily: template.font }}
                      >
                        Your Name
                      </p>
                      <p className="text-xs opacity-60" style={{ fontFamily: template.font }}>
                        Job Title
                      </p>
                    </div>
                  </div>

                  {/* Template Info */}
                  <div className="p-4 bg-white border-t border-border/30">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{template.name}</p>
                        <p className="text-xs text-muted-foreground">{template.category}</p>
                      </div>
                      <div
                        className="w-5 h-5 rounded-full shadow-sm"
                        style={{ backgroundColor: template.primary_color }}
                      />
                    </div>
                  </div>
                </button>
              </motion.div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!isLoading && filteredTemplates.length === 0 && (
          <div className="text-center py-24">
            <p className="text-muted-foreground">No templates found in this category.</p>
          </div>
        )}
      </main>

      {/* Bottom Action Bar (Mobile) */}
      {selectedTemplate && (
        <motion.div
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          className="fixed bottom-0 left-0 right-0 p-4 glass border-t border-border md:hidden"
        >
          <Button
            className="w-full h-14 rounded-xl bg-primary text-primary-foreground font-medium"
            onClick={handleContinue}
            data-testid="mobile-continue-btn"
          >
            <Sparkles className="w-5 h-5 mr-2" />
            Continue with {selectedTemplate.name}
          </Button>
        </motion.div>
      )}
    </div>
  );
};

export default Templates;
